import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import type { ExternalIdentifier } from 'types/writer'

import type { SearchableDropdownOption } from 'components/ds/SearchableDropdown'
import { SearchableDropdown } from 'components/ds/SearchableDropdown'
import { CreateNewAction } from 'components/ds/SearchableDropdown/CreateNewAction'
import { OptionWithEditIcon } from 'components/ds/SearchableDropdown/OptionWithEditIcon'

import { RIGHTS_DROPDOWN_WIDTH } from '../../constants'
import { formatIdentifiersForDisplay } from '../../utils/identifier-validation'
import { checkIfLoading, getOptions } from '../dropdown-utils'

export interface Publisher {
  id: number
  name: string
  external_identifiers?: ExternalIdentifier[]
}

export interface PublisherDropdownProps {
  /** Current selected publisher ID */
  value?: string
  /** Callback when a publisher is selected */
  onSelect: (option: SearchableDropdownOption<Publisher>) => void
  /** Preloaded publishers from Redux store */
  publishers?: Publisher[]
  /** Publisher IDs to prioritize at the top of the list (e.g., writer's associated publishers) */
  priorityIds?: number[]
  /** Placeholder text for the search input */
  placeholder?: string
  /** Whether the dropdown is disabled */
  disabled?: boolean
  /** Optional ARIA label for accessibility */
  'aria-label'?: string
  /** Split ID to identify which split row triggered the creation */
  splitId?: string | number
  /** Splits key (track ID or BULK_EDIT_KEY) for updating the correct split after creation */
  splitsKey?: number | string
}

/**
 * PublisherDropdown - A specialized dropdown for selecting publishers
 *
 * This component uses the SearchableDropdown component to provide a searchable
 * interface for selecting publishers. It fetches publisher data from the API and
 * displays each option with an edit icon.
 *
 * Features:
 * - Searchable publisher selection
 * - Edit icon for each publisher (links to edit route when implemented)
 * - "Create new publisher" action button
 *
 * @param props - Component props
 * @returns PublisherDropdown component
 *
 * @example
 * ```tsx
 * <PublisherDropdown
 *   value={selectedPublisherId}
 *   onSelect={(option) => handlePublisherSelect(option)}
 *   placeholder="Select publisher..."
 * />
 * ```
 */
export function PublisherDropdown({
  value,
  onSelect,
  publishers = [],
  priorityIds = [],
  placeholder = 'Publisher',
  disabled = false,
  'aria-label': ariaLabel = 'Publisher dropdown',
  splitId,
  splitsKey,
}: PublisherDropdownProps) {
  const navigate = useNavigate()

  const options = getOptions(priorityIds, publishers)
  const isLoading = checkIfLoading(value, options)

  // Custom option renderer with edit icon
  const renderOption = useCallback(
    (option: SearchableDropdownOption<Publisher>) => {
      const secondaryText = formatIdentifiersForDisplay(
        option.data?.external_identifiers,
      )
      return (
        <OptionWithEditIcon
          label={option.label}
          secondaryText={secondaryText}
          onEditClick={() => {
            // Navigate to edit publisher modal
            // Include splitId and splitsKey for auto-selection after edit
            navigate(`rights-data/publishers/${option.id}/edit`, {
              state: { splitId, splitsKey },
            })
          }}
        />
      )
    },
    [navigate, splitId, splitsKey],
  )

  // "Create new publisher" action component with relative path
  // Use function form to receive search value from SearchableDropdown
  const actionComponent = useCallback(
    (searchValue: string) => (
      <CreateNewAction
        label="Create new publisher"
        to="rights-data/publishers/new"
        searchText={searchValue}
        splitId={splitId}
        splitsKey={splitsKey}
      />
    ),
    [splitId, splitsKey],
  )

  return (
    <SearchableDropdown
      value={value}
      onSelect={onSelect}
      options={options}
      loading={isLoading}
      placeholder={placeholder}
      renderOption={renderOption}
      actionComponent={actionComponent}
      aria-label={ariaLabel}
      disabled={disabled}
      maxHeight={208}
      customDropdownWidth={RIGHTS_DROPDOWN_WIDTH}
    />
  )
}

export default PublisherDropdown
